import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { FacebookTemplateVariableMappingEditor } from '@/components/settings/FacebookTemplateVariableMappingEditor'
import {
  buildCampaignSourceOptions,
  buildFacebookFormSourceOptions,
  buildFacebookVariableMapping,
  countMetaTemplateBodyVariables,
  normalizeFacebookVariableMapping,
  normalizeFacebookVariableMappingFromApi,
  type FacebookLeadgenQuestion,
  type FacebookVariableMappingEntry,
  type MetaTemplateLike,
} from '@/components/settings/facebookTemplateMapping'
import { useAuth } from '@/hooks/use-auth'

export type FacebookTemplateLinkRecord = {
  form_id?: string
  campaign_id?: string
  template_name: string
  template_language: string
  waba_id: string
  variable_mapping?: FacebookVariableMappingEntry[]
  form_questions_snapshot?: FacebookLeadgenQuestion[]
}

type FacebookTemplateMappingEditDialogProps = {
  target: 'form' | 'campaign'
  link: FacebookTemplateLinkRecord | null
  formQuestions?: FacebookLeadgenQuestion[]
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface Template extends MetaTemplateLike {
  status: string
}

export function FacebookTemplateMappingEditDialog({
  target,
  link,
  formQuestions,
  isOpen,
  onClose,
  onSuccess,
}: FacebookTemplateMappingEditDialogProps) {
  const { token } = useAuth()
  const [variableMapping, setVariableMapping] = useState<FacebookVariableMappingEntry[]>([])
  const [templateBody, setTemplateBody] = useState<string | null>(null)
  const [campaignQuestions, setCampaignQuestions] = useState<FacebookLeadgenQuestion[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingFields, setLoadingFields] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const questions = useMemo(() => {
    if (target === 'form') {
      return formQuestions ?? link?.form_questions_snapshot ?? []
    }
    if (campaignQuestions.length > 0) {
      return campaignQuestions
    }
    return link?.form_questions_snapshot ?? []
  }, [target, formQuestions, link?.form_questions_snapshot, campaignQuestions])

  const sourceOptions = useMemo(() => {
    if (target === 'form' || questions.length > 0) {
      return buildFacebookFormSourceOptions(questions)
    }
    return buildCampaignSourceOptions()
  }, [target, questions])

  useEffect(() => {
    if (!isOpen || !link) {
      return
    }

    let cancelled = false

    const loadCampaignFields = async () => {
      if (target !== 'campaign' || !link.campaign_id) {
        setCampaignQuestions([])
        return
      }
      setLoadingFields(true)
      try {
        const response = await fetch(
          `/api/v1/leads/meta/campaign-fields/?campaign_id=${encodeURIComponent(link.campaign_id)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (!response.ok) {
          if (!cancelled) setCampaignQuestions([])
          return
        }
        const data = await response.json()
        const fetched = Array.isArray(data?.questions) ? (data.questions as FacebookLeadgenQuestion[]) : []
        if (!cancelled) {
          setCampaignQuestions(fetched)
        }
      } catch {
        if (!cancelled) setCampaignQuestions([])
      } finally {
        if (!cancelled) setLoadingFields(false)
      }
    }

    const loadTemplate = async () => {
      setLoading(true)
      setError(null)
      try {
        await loadCampaignFields()
        const response = await fetch(`/api/v1/leads/meta/templates/?waba_id=${encodeURIComponent(link.waba_id)}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) {
          throw new Error('No se pudieron cargar las plantillas de Meta.')
        }

        const data = await response.json()
        const templates: Template[] = data.data || []
        const template = templates.find(
          (item) => item.name === link.template_name && item.language === link.template_language,
        )
        if (!template) {
          throw new Error('La plantilla vinculada ya no está disponible en la cuenta de WhatsApp.')
        }

        const bodyText =
          template.components?.find((component) => component.type?.toUpperCase() === 'BODY')?.text ?? null
        const count = countMetaTemplateBodyVariables(template)
        const previous = normalizeFacebookVariableMappingFromApi(link.variable_mapping)

        if (!cancelled) {
          setTemplateBody(bodyText)
          setVariableMapping(buildFacebookVariableMapping(count, previous))
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error al cargar la plantilla')
          setVariableMapping([])
          setTemplateBody(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadTemplate()
    return () => {
      cancelled = true
      setCampaignQuestions([])
    }
  }, [isOpen, link, target, token])

  const handleSave = async () => {
    if (!link) return

    const normalizedMapping = normalizeFacebookVariableMapping(variableMapping)
    if (variableMapping.length > 0 && normalizedMapping.length !== variableMapping.length) {
      setError('Completa el mapeo de todas las variables de la plantilla.')
      return
    }

    const entityId = target === 'form' ? link.form_id : link.campaign_id
    if (!entityId) {
      setError('Identificador de link no disponible.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const basePath = target === 'form' ? '/api/v1/leads/meta/form-links/' : '/api/v1/leads/meta/links/'
      const queryKey = target === 'form' ? 'form_id' : 'campaign_id'
      const payload: Record<string, unknown> = {
        variable_mapping: normalizedMapping,
      }
      if ((target === 'form' || target === 'campaign') && questions.length > 0) {
        payload.form_questions_snapshot = questions
      }

      const response = await fetch(`${basePath}?${queryKey}=${encodeURIComponent(entityId)}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const mappingError = Array.isArray(errorData.variable_mapping) ? errorData.variable_mapping[0] : null
        throw new Error(mappingError || errorData.error || 'No se pudo guardar el mapeo')
      }

      onSuccess()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el mapeo')
    } finally {
      setSubmitting(false)
    }
  }

  const title = target === 'form' ? 'Editar variables del formulario' : 'Editar variables de la campaña'

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {link ? (
            <div className="grid gap-2">
              <Label>Plantilla vinculada</Label>
              <div className="rounded-md border bg-muted p-2 text-sm">
                {link.template_name} ({link.template_language})
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Cargando plantilla…
            </div>
          ) : (
            <>
              {templateBody ? (
                <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground whitespace-pre-wrap">
                  {templateBody}
                </p>
              ) : null}

              <FacebookTemplateVariableMappingEditor
                mapping={variableMapping}
                sourceOptions={sourceOptions}
                onChange={setVariableMapping}
                showHelp
                description={
                  loadingFields
                    ? 'Cargando variables de la campaña…'
                    : target === 'campaign' && questions.length === 0
                      ? 'Esta campaña no expone campos de formulario. Usa datos del lead o contexto de Meta.'
                      : undefined
                }
              />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSave()} disabled={submitting || loading || !link}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Guardar mapeo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
